import React, { useState } from 'react';

import { IconButton, MenuItem, Stack } from '@mui/material';
import { Localization } from '@mui/material/locale';

import { LANGUAGES } from '../../constants';
import { useThemeMode } from '../../providers/ThemeModeProvider';
import { Image } from '../Image/Image';
import { MenuPopover } from '../MenuPopover/MenuPopover';

export interface LanguagePopoverProps {
  selectedLanguageOption: LanguageOption;
  onChangeLanguageOption: (value: typeof LANGUAGES[keyof typeof LANGUAGES]) => void;
  options: LanguageOption[];
}

export interface LanguageOption {
  label: string;
  value: typeof LANGUAGES[keyof typeof LANGUAGES];
  systemValue: Localization;
  icon: string;
}

export const LanguagePopover = ({ selectedLanguageOption, onChangeLanguageOption, options }: LanguagePopoverProps) => {
  const { themeMode } = useThemeMode();

  const [open, setOpen] = useState<HTMLElement | null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  return (
    <>
      <IconButton
        data-testid="language-menu-button"
        onClick={handleOpen}
        sx={{
          ...(open && { bgcolor: themeMode === 'light' ? 'grey.300' : 'grey.700' }),
        }}
      >
        <Image src={selectedLanguageOption.icon} />
      </IconButton>

      <MenuPopover
        data-testid="language-popover"
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        color={themeMode === 'light' ? 'grey.200' : 'grey.800'}
        sx={{
          width: 232,
          border: '1px solid',
          borderColor: (theme) => (theme.palette.mode === 'light' ? 'grey.300' : 'grey.700'),
          boxShadow: '0px 4px 40px rgba(22, 28, 36, 0.15)',
        }}
      >
        <Stack spacing={0.75}>
          {options.map((option: LanguageOption) => (
            <MenuItem
              sx={{ height: 48 }}
              key={option.value}
              selected={option.value === selectedLanguageOption.value}
              onClick={() => {
                onChangeLanguageOption(option.value);
                handleClose();
              }}
            >
              <Image src={option.icon} sx={{ mr: 3 }} />
              {option.label}
            </MenuItem>
          ))}
        </Stack>
      </MenuPopover>
    </>
  );
};
